import { CommonConstant } from '@utility/constants/common';
import {
  Fragment,
  cloneElement,
  forwardRef,
  isValidElement,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { AppTableRowPlaceholder } from './Placeholder';
import { TablePagination } from './TablePagination';
import {
  AppTableBody,
  AppTableCell,
  AppTableFooter,
  AppTableHeader,
  AppTableHeaderCell,
  AppTableRoot,
  AppTableRow,
  type AppTableCellProps,
  type AppTableRootProps,
  type AppTableRowProps,
} from './AppTablePrimitives';
import type { ISortObject, ITableColumn } from 'types/common/table';

export * from './AppTablePrimitives';

const ASCENDING = CommonConstant.sortDirection.ascending;
const DESCENDING = CommonConstant.sortDirection.descending;

interface IColumnProps {
  customProps?: Record<string, any>;
  column: ITableColumn;
  sort?: ISortObject;
  sortChanged: (key: string) => void;
  headerOffSet?: number;
}

function Columns({
  column,
  sort,
  sortChanged,
  customProps = {},
}: IColumnProps) {
  const otherPropsKey = column.componentPropsKey;
  const sortKey = column.sortKey;
  const sortable = !!sortKey && !column.hideSort;
  const direction = sortKey ? sort?.[sortKey] : undefined;
  const sorted =
    sortable && direction
      ? direction === ASCENDING
        ? 'ascending'
        : 'descending'
      : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLTableCellElement>) => {
    if (sortKey && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      sortChanged(sortKey);
    }
  };

  const style: CSSProperties = {
    ...(column.bgcolor ? { background: column.bgcolor } : {}),
    ...(column.textcolor ? { color: column.textcolor } : {}),
    ...column.style,
  };

  return (
    <AppTableHeaderCell
      width={column.width}
      textAlign={column.textAlign || 'center'}
      sorted={sorted}
      onClick={() => sortKey && sortChanged(sortKey)}
      onKeyDown={sortable ? onKeyDown : undefined}
      tabIndex={sortable ? 0 : undefined}
      className={cn(sortable && 'sk-app-table__sortable', column.className)}
      style={style}
    >
      {column.component
        ? cloneElement(column.component, {
            ...(column.component.props || {}),
            ...((otherPropsKey && customProps[otherPropsKey]) || {}),
          })
        : column.text}
      {sortable && (
        <span
          aria-hidden="true"
          className="sk-app-table__caret"
          data-sorted={sorted ? '' : undefined}
        >
          {sorted === 'descending' ? (
            <CaretDownIcon size={11} weight="bold" />
          ) : (
            <CaretUpIcon size={11} weight="bold" />
          )}
        </span>
      )}
    </AppTableHeaderCell>
  );
}

interface IRowProps<T> {
  item: T;
  index: number;
}

/*
 * Semantic UI table props screens still pass. They map onto AppTableRoot's
 * modifiers (celled, compact, striped, fixed, singleLine) or do nothing
 * (padded, unstackable, rounded, sticky).
 */
type LegacyTableProps = Pick<
  AppTableRootProps,
  | 'celled'
  | 'compact'
  | 'striped'
  | 'fixed'
  | 'singleLine'
  | 'selectable'
  | 'padded'
  | 'unstackable'
  | 'stackable'
  | 'collapsing'
  | 'className'
  | 'style'
> & {
  /** No card frame around the table. */
  basic?: boolean | 'very';
  /** The header scrolls away with the rows instead of sticking. */
  staticHeader?: boolean;
  rounded?: boolean;
  sticky?: boolean;
};

interface IAppTableProps<T = any> extends LegacyTableProps {
  AdditionalColumnRowComponent?: React.ReactElement;
  currentPage?: number;
  customProps?: Record<string, any>;
  columns?: Array<ITableColumn>;
  data?: Array<any>;
  descriptionText?: string;
  enableScrollToTop?: boolean;
  footerComponent?: React.ReactElement;
  headerOffSet?: number;
  HeaderRowComponent?: React.ReactElement;
  keyField?: string;
  loading?: boolean;
  loadingRows?: number;
  noDataMessage?: string | React.ReactNode;
  noShadow?: boolean;
  pageChanged?: (page: number) => void;
  paginationUpward?: boolean;
  pageSize?: number;
  pageSizeChanged?: (size: number) => void;
  RowComponent?: React.ReactElement<IRowProps<T>>;
  showHeader?: boolean;
  sortable?: boolean;
  sort?: ISortObject;
  sortChanged?: (newSort: ISortObject, key: string) => void;
  structured?: boolean;
  tableKey?: string;
  textAlign?: 'left' | 'center' | 'right';
  totalData?: number;
  totalPages?: number;
  wrapperClass?: string;
}

export function AppTable<T = any>({
  AdditionalColumnRowComponent,
  currentPage,
  customProps,
  columns = [],
  data,
  descriptionText = '',
  enableScrollToTop = false,
  footerComponent,
  headerOffSet = 0,
  HeaderRowComponent,
  keyField = '_id',
  loading,
  loadingRows = 4,
  noDataMessage = 'No Data Found',
  noShadow,
  pageChanged,
  paginationUpward = true,
  pageSize,
  pageSizeChanged,
  RowComponent,
  showHeader = true,
  sortable: _sortable,
  sort,
  sortChanged,
  structured,
  tableKey = '',
  textAlign = 'center',
  totalData,
  totalPages,
  wrapperClass,
  basic,
  staticHeader,
  rounded: _rounded,
  sticky: _sticky,
  className,
  style,
  ...rest
}: IAppTableProps<T>) {
  const myRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = () => {
    if (myRef?.current) {
      const yOffset = -80;
      const elementFromTop = myRef.current.getBoundingClientRect().top;
      const y = elementFromTop + window.pageYOffset + yOffset;

      const mainContent = document.getElementById('main-skupreme-content');
      mainContent?.scrollTo({ top: y });
    }
  };

  useDebouncedEffect(
    () => {
      if (enableScrollToTop) scrollToTop();
    },
    [data, myRef, enableScrollToTop],
    200,
  );

  const orderChanged = (key: string) => {
    let direction: string;

    if (!sort?.[key]) {
      const col = columns.find(x => x.sortKey === key);
      direction = col?.firstDirection || ASCENDING;
    } else {
      direction = sort[key] === ASCENDING ? DESCENDING : ASCENDING;
    }

    sortChanged?.({ [key]: direction } as ISortObject, key);
  };

  const showPagination = (currentPage || 0) > 0 && (totalData || 0) > 10;

  return (
    <div
      ref={myRef}
      className={cn(
        'sk-app-table-wrap sk:mt-4 sk:max-w-full',
        !basic && 'sk:rounded-card sk:bg-surface',
        !basic && !noShadow && 'sk:shadow-card',
        wrapperClass,
      )}
    >
      <AppTableRoot
        basic
        selectable
        celled={structured}
        data-field-value={tableKey}
        className={cn(!staticHeader && 'sk-app-table--sticky', className)}
        style={
          {
            '--sk-app-table-offset': `${headerOffSet}px`,
            ...style,
          } as CSSProperties
        }
        {...rest}
      >
        {showHeader && (
          <AppTableHeader>
            {AdditionalColumnRowComponent &&
              cloneElement(AdditionalColumnRowComponent, {
                ...(AdditionalColumnRowComponent.props || {}),
              })}
            <AppTableRow textAlign={textAlign} verticalAlign="middle">
              {columns.map(item => (
                <Columns
                  headerOffSet={headerOffSet}
                  sort={sort}
                  customProps={customProps}
                  sortChanged={orderChanged}
                  key={item.key}
                  column={item}
                />
              ))}
            </AppTableRow>
          </AppTableHeader>
        )}
        <AppTableBody className="table-body">
          {HeaderRowComponent &&
            cloneElement(HeaderRowComponent, {
              ...(HeaderRowComponent.props || {}),
            })}
          {loading ? (
            <AppTableRowPlaceholder
              rows={data?.length || loadingRows}
              colSpan={columns.length}
            />
          ) : (
            (data || []).map((item, index) => (
              <Fragment key={String(item[keyField])}>
                {isValidElement(RowComponent) &&
                  cloneElement(RowComponent, {
                    ...(RowComponent.props || {}),
                    item,
                    index,
                  })}
              </Fragment>
            ))
          )}
        </AppTableBody>
        {!loading && (data || []).length === 0 && (
          <AppTableFooter>
            <AppTableRow>
              <AppTableCell
                colSpan={columns.length || 1}
                textAlign="center"
                className="sk:py-8 sk:text-fg-tertiary"
              >
                {noDataMessage}
              </AppTableCell>
            </AppTableRow>
          </AppTableFooter>
        )}
      </AppTableRoot>
      {(footerComponent || showPagination) && (
        <TablePagination
          noPadding={!!basic}
          colSpan={columns.length}
          currentPage={currentPage ?? 1}
          totalPages={totalPages ?? 0}
          pageChanged={pageChanged ?? (() => {})}
          pageSizeChanged={pageSizeChanged ?? (() => {})}
          pageSize={pageSize ?? 10}
          footerComponent={footerComponent}
          totalData={totalData ?? 0}
          descriptionText={descriptionText}
          upward={paginationUpward}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------- shared row variants -- */

/** Tints on hover, or while it carries the `hover` class. */
export const HoverTableRow = forwardRef<HTMLTableRowElement, AppTableRowProps>(
  function HoverTableRowPart({ className, ...props }, ref) {
    return (
      <AppTableRow
        ref={ref}
        {...props}
        className={cn('sk-app-table__row--hover', className)}
      />
    );
  },
);

/**
 * Tints on hover and shows its `.more-details` and `.action-container`
 * controls; `.action-cell` is their positioning context.
 */
export const ActionHoverTableRow = forwardRef<
  HTMLTableRowElement,
  AppTableRowProps
>(function ActionHoverTableRowPart({ className, ...props }, ref) {
  return (
    <AppTableRow
      ref={ref}
      {...props}
      className={cn(
        'sk-app-table__row--hover sk-app-table__row--action-hover',
        className,
      )}
    />
  );
});

/**
 * Shows `.more-details` on hover and `.action-container` while it carries
 * the `hover` class.
 */
export const ActionHoverRow = forwardRef<HTMLTableRowElement, AppTableRowProps>(
  function ActionHoverRowPart({ className, ...props }, ref) {
    return (
      <AppTableRow
        ref={ref}
        {...props}
        className={cn('sk-app-table__row--action', className)}
      />
    );
  },
);

/** Shows its BorderTableCells' `.border` bar on hover; `catalog-row` also tints. */
export const BorderTableRow = forwardRef<HTMLTableRowElement, AppTableRowProps>(
  function BorderTableRowPart({ className, ...props }, ref) {
    return (
      <AppTableRow
        ref={ref}
        {...props}
        className={cn('sk-app-table__row--border', className)}
      />
    );
  },
);

export type BorderTableCellProps = AppTableCellProps & {
  /** `'true'` keeps the `.border` bar showing. */
  expand?: string | boolean;
};

/** Positions a `.border` child as a bar down its left edge. */
export const BorderTableCell = forwardRef<
  HTMLTableCellElement,
  BorderTableCellProps
>(function BorderTableCellPart({ className, expand, ...props }, ref) {
  return (
    <AppTableCell
      ref={ref}
      {...props}
      data-expand={expand === undefined ? undefined : String(expand)}
      className={cn('sk-app-table__border-cell', className)}
    />
  );
});

/* Moved to its own file; re-exported for the screens that import it from here. */
export { TableHeaderToolTip } from './TableHeaderToolTip';
